import Cookies from "js-cookie";
import { UserRole } from "../../enums";
import { IStudioSelectItemModel } from "../models/studioSelectItemModel";

/**
 * Transparency for studio owners: a global admin opening someone's studio has to
 * confirm it first, and every confirmed opening lands in the owner's
 * "Моят профил" → "Достъп от Planny" log. Training (test) studios are exempt.
 */

/**
 * Remembers which studio the admin last confirmed in this browser session, so
 * reopening the app straight into that studio does not ask again. A session
 * cookie (shared by all tabs, gone when the browser closes) that also stops
 * counting after STUDIO_ACCESS_CONFIRMATION_TTL_MS, for browsers that restore
 * session cookies on restart.
 */
export const STUDIO_ACCESS_CONFIRMATION_COOKIE = "StudioAccessConfirmed";

export const STUDIO_ACCESS_CONFIRMATION_TTL_MS = 12 * 60 * 60 * 1000;

/** Third cookie segment: the studio was opened through the long-press back door, unrecorded. */
const SILENT_ACCESS_MARKER = "silent";

type GatedStudio = Pick<IStudioSelectItemModel, "id" | "isTraining">;

/** Whether opening this studio, for this role, has to be confirmed and logged. */
export const isGatedStudioAccess = (
  role: UserRole | undefined,
  studio: GatedStudio | undefined,
): studio is GatedStudio =>
  role === UserRole.GlobalAdmin && !!studio && !studio.isTraining;

/** Whether switching to `targetStudio` has to go through the confirmation dialog. */
export const requiresStudioSwitchConfirmation = (
  role: UserRole | undefined,
  targetStudio: GatedStudio | undefined,
  currentStudioId: number,
  now: Date = new Date(),
): boolean =>
  isGatedStudioAccess(role, targetStudio) &&
  targetStudio.id !== currentStudioId &&
  // Re-picking the studio already confirmed this session once the selection was cleared
  // (saving the studio wizard does that) is the same visit, not a new opening.
  !(currentStudioId === 0 && isStudioAccessConfirmed(targetStudio.id, now));

export const markStudioAccessConfirmed = (studioId: number, now: Date = new Date()) => {
  Cookies.set(STUDIO_ACCESS_CONFIRMATION_COOKIE, `${studioId}|${now.getTime()}`);
};

/**
 * Same as markStudioAccessConfirmed, for the long-press back door: the opening was not
 * recorded, so leaving the studio must not be either — the owner would see an exit with
 * no opening before it.
 */
export const markStudioAccessConfirmedSilently = (studioId: number, now: Date = new Date()) => {
  Cookies.set(
    STUDIO_ACCESS_CONFIRMATION_COOKIE,
    `${studioId}|${now.getTime()}|${SILENT_ACCESS_MARKER}`,
  );
};

/**
 * Whether the studio was opened through the long-press back door. Deliberately ignores the
 * 12-hour expiry: an unrecorded visit stays unrecorded however long it lasts.
 */
export const isStudioAccessSilent = (studioId: number): boolean => {
  const [storedStudioId, , marker] = (Cookies.get(STUDIO_ACCESS_CONFIRMATION_COOKIE) ?? "").split(
    "|",
  );

  return Number(storedStudioId) === studioId && marker === SILENT_ACCESS_MARKER;
};

export const isStudioAccessConfirmed = (studioId: number, now: Date = new Date()): boolean => {
  const [storedStudioId, confirmedAt] = (Cookies.get(STUDIO_ACCESS_CONFIRMATION_COOKIE) ?? "")
    .split("|")
    .map(Number);

  if (!storedStudioId || !confirmedAt || storedStudioId !== studioId) {
    return false;
  }

  const age = now.getTime() - confirmedAt;
  return age >= 0 && age < STUDIO_ACCESS_CONFIRMATION_TTL_MS;
};

export const clearStudioAccessConfirmation = () => {
  Cookies.remove(STUDIO_ACCESS_CONFIRMATION_COOKIE);
};
